import {SelectionModel} from '@angular/cdk/collections';
import {Component, ElementRef, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {ContactModel} from '@app/models/contact.model';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {Contact} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';

export enum ImportStatus {
  READY,
  IMPORTED_OK,
  EXISTING,
  MISSING_NAME,
  MISSING_NUMBER,
  ERROR
}

@Component({
  selector: 'gt-csv-contacts-import',
  templateUrl: './csv-contacts-import.component.html',
  styleUrls: ['./csv-contacts-import.component.scss']
})
export class CsvContactsImportComponent implements OnInit {
  @Output() closing = new EventEmitter<void>();
  @ViewChild('fileInput') fileInput: ElementRef;

  dataSource = new MatTableDataSource<ContactModel>();
  displayedColumns = ['select', 'name', 'number', 'email', 'company', 'status'];
  showHelp = false;
  selection = new SelectionModel<ContactModel>(true, []);
  importStatus = new Map<ContactModel, ImportStatus>();
  IMPORTSTATUS = ImportStatus;

  private columnIndexMobile = 15;
  private columnIndexWorkPhone = 13;
  private columnIndexFirstName = 0;
  private columnIndexLastName = 2;
  private columnIndexEmail = 8;
  private columnIndexEmailFallback = 28;
  private columnIndexCompany = 31;
  private contacts: ContactModel[] = [];

  constructor(
    private authenticationService: GtAuthenticationService,
    private directoryService: GtDirectoryService,
    private translateService: TranslateService,
    private notificationService: GtNotificationService
  ) {}

  ngOnInit() {
    this.directoryService.getContacts(this.authenticationService.oname, false).subscribe(contacts => {
      this.contacts = contacts;
    });
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.filter(contact => this.getImportStatus(contact) === ImportStatus.READY).length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach(contact => {
          if (this.getImportStatus(contact) === ImportStatus.READY) {
            this.selection.select(contact);
          }
        });
  }

  uploadCSV(): void {
    this.showHelp = false;
    this.fileInput.nativeElement.click();
  }

  changeListener($event): void {
    try {
      this.readFile($event.target.files[0]);
      this.fileInput.nativeElement.value = null;
    } catch (error) {
      console.error('Not able to read file ', $event);
    }
  }

  private readFile(file: File) {
    this.dataSource.data = [];
    const reader = new FileReader();
    reader.onload = () => {
      const csvString: string = reader.result as string;
      this.csvToContact(csvString);
    };
    reader.onabort = () => {
      this.notificationService.errorNotification.next(this.translateService.instant('csv_contact_import.error_read'));
    };
    reader.onerror = () => {
      this.notificationService.errorNotification.next(this.translateService.instant('csv_contact_import.error_read'));
    };
    reader.readAsText(file);
  }

  csvToContact(content: string) {
    content = content.replace(/"/g, '');

    const lines: string[] = content.split(/\r?\n/g);
    const contacts: Contact[] = [];

    for (let i = 1; i < lines.length; i++) {
      const currentline: string[] = lines[i].split(',');
      if (currentline.length <= 1) {
        continue;
      }
      const contactData: Contact = {};
      contactData['@type'] = 'Contact';
      contactData.ownerOrganization = this.authenticationService.oname;
      contactData.ownerLogin = this.authenticationService.login;

      contactData.lastName = currentline[this.columnIndexLastName];
      contactData.firstName = currentline[this.columnIndexFirstName];

      let fullNumber: string = currentline[this.columnIndexMobile];
      if (!fullNumber || !fullNumber.length) {
        fullNumber = currentline[this.columnIndexWorkPhone];
      }
      contactData.fullNumber = GtPhoneNumberUtil.toBackend(fullNumber);

      let email: string = currentline[this.columnIndexEmail];
      if (email && email.length) {
        if (email.toLowerCase().indexOf('exchangelabs') > -1) {
          email = currentline[this.columnIndexEmailFallback];
        }
        if (email) {
          contactData.email = email.trim();
        }
      }
      contactData.company = currentline[this.columnIndexCompany];
      contactData.global = true;
      contacts.push(contactData);
    }
    this.dataSource.data = contacts.map(contactData => ContactModel.convertContactModel(contactData));
    this.dataSource.data.forEach(contact => {
      this.setImportStatus(contact);
    });
    this.notificationService.normalNotification.next(
      `${this.translateService.instant('csv_contact_import.success_read')}: ` + this.dataSource.data.length
    );
  }

  private setImportStatus(contact: ContactModel) {
    if (this.contacts.find(c => c.phoneNumber === contact.phoneNumber)) {
      this.importStatus.set(contact, ImportStatus.EXISTING);
    } else if (!contact.name) {
      this.importStatus.set(contact, ImportStatus.MISSING_NAME);
    } else if (!contact.phoneNumber) {
      this.importStatus.set(contact, ImportStatus.MISSING_NUMBER);
    } else {
      this.importStatus.set(contact, ImportStatus.READY);
    }
  }

  readyForImport(contact: ContactModel): boolean {
    return this.importStatus.get(contact) === ImportStatus.READY;
  }

  getImportStatus(contact: ContactModel): ImportStatus {
    return this.importStatus.get(contact);
  }

  saveContact(contact: ContactModel) {
    this.directoryService
      .createContact(this.authenticationService.oname, this.authenticationService.login, contact.getBackendModel())
      .subscribe(
        savedContact => {
          contact.id = savedContact.id;
          this.importStatus.set(contact, ImportStatus.IMPORTED_OK);
        },
        error => {
          if (error.code === 409) {
            this.notificationService.normalNotification.next(
              contact.name + ` ${this.translateService.instant('csv_contact_import.existing_save')}`
            );
            this.importStatus.set(contact, ImportStatus.EXISTING);
          } else {
            this.notificationService.errorNotification.next(
              `${this.translateService.instant('csv_contact_import.error_saving')} ` + contact.name + ': ' + error.message
            );
            this.importStatus.set(contact, ImportStatus.ERROR);
          }
        }
      );
  }

  saveAll() {
    this.selection.selected.forEach(contact => {
      if (this.getImportStatus(contact) === ImportStatus.READY) {
        this.saveContact(contact);
      }
    });
  }

  cancel() {
    this.dataSource.data = [];
  }

  closeDialog() {
    this.closing.emit();
  }
}
